import React, { useState } from 'react';
import { CurrencyAmount } from '../../CurrencyAmount';
import { EditableCell } from '../../ui/EditableCell';
import { EditableColumnHeader } from '../../ui/EditableColumnHeader';
import { AddColumnButton } from '../../ui/AddColumnButton';
import { LabelDefinition } from '@/types/label-budget';
import {
  saveLabelDefinitions,
  updateApiKeyMetadata,
} from '@/utils/label-budget-storage';
import { EmployeeRecord } from '@/utils/employee-indexeddb';
import {
  HEADQUARTER_FILTER_PREFIX,
  DEPARTMENT_FILTER_PREFIX,
} from '@/utils/department-utils';
import {
  ApiKeyUsageDetail,
  UsageDetailsData,
} from '../../../utils/openai-api';
import { ProviderBadge } from './ProviderBadge';
import { WorkspaceDisplay } from './WorkspaceDisplay';
import { ModelUsageContent } from './ApiKeyModelUsage';
import { UserDailyCostChart, USER_DAILY_COST_CHART_RANGES } from './UserDailyCostChart';
import { formatNumber, formatPercentage } from '../utils/formatters';
import { getOrganizationInfo, getOrganizationLines } from '../utils/organization';
import { UsageDataSource } from '../types';
import { FilteredDataResult } from '../hooks/useFilteredData';
import type { ApiKeyMetadata } from '../../../types/label-budget';
import { debugLog } from '../../../utils/debug-log';

interface ApiKeyTableProps {
  filteredData: FilteredDataResult | null;
  data: UsageDetailsData | null;
  filteredSortedApiKeys: ApiKeyUsageDetail[];
  paginatedApiKeys: ApiKeyUsageDetail[];
  currentOffset: number;
  setCurrentOffset: (offset: number) => void;
  pageSize: number;
  loading: boolean;
  expandedApiKey: string | null;
  setExpandedApiKey: (id: string | null) => void;
  sort: { key: string | null; dir: 'asc' | 'desc' };
  handleSort: (key: string) => void;
  labelDefinitions: LabelDefinition[];
  setLabelDefinitions: (defs: LabelDefinition[]) => void;
  apiKeyMetadata: Record<string, Partial<ApiKeyMetadata>>;
  setApiKeyMetadata: React.Dispatch<React.SetStateAction<Record<string, Partial<ApiKeyMetadata>>>>;
  employeeData: Map<string, EmployeeRecord>;
  findEmployeeForApiKey: (apiKey: ApiKeyUsageDetail) => EmployeeRecord | undefined;
  dateRange: string;
  dataSource: UsageDataSource;
  includesAnthropic: boolean;
  normalizedDepartmentFilter: string;
  selectedRole: string;
  formatAmountSync: (amount: number) => string;
}

export const ApiKeyTable: React.FC<ApiKeyTableProps> = ({
  filteredData,
  data,
  filteredSortedApiKeys,
  paginatedApiKeys,
  currentOffset,
  setCurrentOffset,
  pageSize,
  loading,
  expandedApiKey,
  setExpandedApiKey,
  sort,
  handleSort,
  labelDefinitions,
  setLabelDefinitions,
  apiKeyMetadata,
  setApiKeyMetadata,
  employeeData,
  findEmployeeForApiKey,
  dateRange,
  dataSource,
  includesAnthropic,
  normalizedDepartmentFilter,
  selectedRole,
  formatAmountSync,
}) => {
  const [splitKeys, setSplitKeys] = useState<Set<string>>(new Set());
  const shouldShowUserDailyCostChart = USER_DAILY_COST_CHART_RANGES.has(dateRange)
    || (dateRange === 'custom' && (filteredData?.chartDailyUsage?.length ?? 0) >= 7);

  const toggleSplit = (id: string) => {
    setSplitKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const createSortableHeader = (label: string, sortKey: string, extraClass = '') => (
    <th
      className={`px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${extraClass}`}
      onClick={() => handleSort(sortKey)}
    >
      {label}
      {sort.key === sortKey && (
        <span className="ml-1">
          {sort.dir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  const handleDeleteLabel = (labelId: string) => {
    if (window.confirm('Are you sure you want to delete this label?')) {
      const filtered = labelDefinitions.filter(def => def.id !== labelId);
      const reordered = filtered.map((def, index) => ({ ...def, order: index }));
      saveLabelDefinitions(reordered);
      setLabelDefinitions(reordered);
      window.dispatchEvent(new CustomEvent('labelSettingsChanged'));
    }
  };

  const handleReorderLabels = (dragIndex: number, dropIndex: number) => {
    const reordered = [...labelDefinitions];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    const updated = reordered.map((def, index) => ({ ...def, order: index }));
    saveLabelDefinitions(updated);
    setLabelDefinitions(updated);
    window.dispatchEvent(new CustomEvent('labelSettingsChanged'));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-2 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1 flex-wrap">
            <svg className="w-4 h-4 flex-shrink-0 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
              API Key Details ({filteredSortedApiKeys.length} keys)
            </h2>
            <button
              onClick={async (event) => {
                const escapeForTable = (str: string) => str.replace(/\|/g, '\\|');
                const escapeHtml = (str: string) => str
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#39;');

                const summaryEntries = filteredSortedApiKeys
                  .map(apiKey => ({ apiKey, employee: findEmployeeForApiKey(apiKey) }))
                  .filter(({ employee }) => !!employee);

                const totalInScope = summaryEntries.length;
                const activeInScope = summaryEntries.filter(({ apiKey }) => apiKey.requests > 0).length;
                const unusedInScope = totalInScope - activeInScope;
                const totalRequests = summaryEntries.reduce((sum, { apiKey }) => sum + (apiKey.requests || 0), 0);
                const totalCost = summaryEntries.reduce((sum, { apiKey }) => sum + (apiKey.cost || 0), 0);
                const usageRate = totalInScope > 0 ? (activeInScope / totalInScope) * 100 : 0;

                let scopeLabel = '';
                if (normalizedDepartmentFilter.startsWith(DEPARTMENT_FILTER_PREFIX)) {
                  const [, hq, dept] = normalizedDepartmentFilter.split('::');
                  if (hq && dept) {
                    scopeLabel = `${hq} / ${dept}`;
                  } else if (hq) {
                    scopeLabel = hq;
                  }
                } else if (normalizedDepartmentFilter.startsWith(HEADQUARTER_FILTER_PREFIX)) {
                  scopeLabel = normalizedDepartmentFilter.slice(HEADQUARTER_FILTER_PREFIX.length);
                } else if (normalizedDepartmentFilter === 'no-usage') {
                  scopeLabel = '未使用者';
                } else if (normalizedDepartmentFilter === 'no-mapping') {
                  scopeLabel = '未紐付け';
                }

                const summaryLines: string[] = [];
                let summaryHtml = '';
                if (scopeLabel) {
                  const roleLabel = selectedRole === '' ? null : selectedRole;
                  summaryLines.push(`# ${scopeLabel} 利用状況`);
                  summaryLines.push(`- 利用率: ${activeInScope}/${totalInScope} (${usageRate.toFixed(1)}%)`);
                  summaryLines.push(`- 利用者数: ${activeInScope} / 未利用者数: ${unusedInScope}`);
                  summaryLines.push(`- 合計リクエスト: ${formatNumber(totalRequests)} / 合計コスト: ${formatAmountSync(totalCost)}`);
                  if (roleLabel) {
                    summaryLines.push(`- Role フィルター: ${roleLabel}`);
                  }
                  summaryLines.push('');

                  summaryHtml = `
                    <div>
                      <p><strong>${escapeHtml(scopeLabel)} 利用状況</strong></p>
                      <ul>
                        <li>利用率: ${activeInScope}/${totalInScope} (${usageRate.toFixed(1)}%)</li>
                        <li>利用者数: ${activeInScope} / 未利用者数: ${unusedInScope}</li>
                        <li>合計リクエスト: ${escapeHtml(formatNumber(totalRequests))} / 合計コスト: ${escapeHtml(formatAmountSync(totalCost))}</li>
                        ${roleLabel ? `<li>Role フィルター: ${escapeHtml(roleLabel)}</li>` : ''}
                      </ul>
                    </div>
                  `;
                }

                const header = '| NAME | ROLE | WORKSPACE | HEADQUARTER | DEPARTMENT | REQUESTS | COST |';
                const separator = '|------|------|-----------|-------------|------------|----------|------|';

                const tableRowsPlain: string[] = [];
                const tableRowsHtml: string[] = [];

                paginatedApiKeys.forEach(apiKey => {
                  const employee = findEmployeeForApiKey(apiKey);
                  const orgInfo = getOrganizationInfo(employee);
                  const name = employee?.name || apiKey.name;
                  const role = employee?.role || '-';
                  const workspaceNames = apiKey.projectName
                    ? apiKey.projectName.split(',').map(value => value.trim()).filter(Boolean)
                    : [];
                  const workspaceIdsList = Array.isArray(apiKey.projectIds) && apiKey.projectIds.length > 0
                    ? apiKey.projectIds
                    : apiKey.projectId
                      ? [apiKey.projectId]
                      : [];
                  const workspace = workspaceNames.length > 0
                    ? workspaceNames.join(', ')
                    : workspaceIdsList.length > 0
                      ? workspaceIdsList.join(', ')
                      : '-';
                  const headquarter = orgInfo.headquarter || '-';
                  const department = orgInfo.departmentUnit || '-';
                  const requests = formatNumber(apiKey.requests || 0);
                  const costValue = apiKey.requests === 0
                    ? '未使用'
                    : formatAmountSync(apiKey.cost || 0);

                  tableRowsPlain.push(`| ${escapeForTable(name)} | ${escapeForTable(role)} | ${escapeForTable(workspace)} | ${escapeForTable(headquarter)} | ${escapeForTable(department)} | ${requests} | ${escapeForTable(costValue)} |`);

                  tableRowsHtml.push(`
                    <tr>
                      <td>${escapeHtml(name)}</td>
                      <td>${escapeHtml(role)}</td>
                      <td>${escapeHtml(workspace)}</td>
                      <td>${escapeHtml(headquarter)}</td>
                      <td>${escapeHtml(department)}</td>
                      <td>${escapeHtml(requests)}</td>
                      <td>${escapeHtml(costValue)}</td>
                    </tr>
                  `);
                });

                const plainText = [...summaryLines, header, separator, ...tableRowsPlain].join('\n');
                const htmlTable = `
                  <table border="1" cellspacing="0" cellpadding="4">
                    <thead>
                      <tr>
                        <th>NAME</th><th>ROLE</th><th>WORKSPACE</th><th>HEADQUARTER</th><th>DEPARTMENT</th><th>REQUESTS</th><th>COST</th>
                      </tr>
                    </thead>
                    <tbody>${tableRowsHtml.join('')}</tbody>
                  </table>
                `;
                const htmlContent = `${summaryHtml}${htmlTable}`;

                try {
                  if (navigator.clipboard && 'write' in navigator.clipboard && typeof ClipboardItem !== 'undefined') {
                    const clipboardItems: Record<string, Blob> = {
                      'text/plain': new Blob([plainText], { type: 'text/plain' }),
                      'text/html': new Blob([htmlContent], { type: 'text/html' })
                    };
                    await navigator.clipboard.write([new ClipboardItem(clipboardItems)]);
                  } else if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(plainText);
                  } else {
                    throw new Error('Clipboard API not supported');
                  }

                  const button = event.currentTarget as HTMLButtonElement;
                  const originalText = button.textContent;
                  button.textContent = 'Copied!';
                  setTimeout(() => {
                    button.textContent = originalText;
                  }, 2000);
                } catch (error) {
                  console.error('Failed to copy summary table:', error);
                }
              }}
              className="ml-1 sm:ml-3 px-2 sm:px-3 py-1 text-[10px] sm:text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
              title="Copy table data with summary (Teams-ready)"
            >
              <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>
            {(data || filteredData) && (
              <div className="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Showing {(filteredData?.displayedApiKeys ?? data?.displayedApiKeys) ?? 0} of {(filteredData?.totalApiKeys ?? data?.totalApiKeys) ?? 0} total API keys
                {employeeData.size > 0 && (
                  <span className="ml-2">
                    • {filteredSortedApiKeys.filter(apiKey => {
                      if (employeeData.has(apiKey.id)) return true;
                      if (apiKey.name.includes('@')) {
                        const apiKeyEmail = apiKey.name.toLowerCase();
                        if (employeeData.has(apiKeyEmail)) return true;
                        const emailPrefix = apiKeyEmail.split('@')[0];
                        if (employeeData.has(emailPrefix)) return true;
                      }
                      return false;
                    }).length} with employee data
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-[10px] sm:text-xs text-green-600 dark:text-green-400">
            {dataSource === 'all'
              ? 'Request counts combine OpenAI Completions API and Anthropic Usage API data'
              : dataSource === 'anthropic'
                ? 'Request counts sourced from Anthropic Usage API'
                : 'Request counts sourced from OpenAI Completions API'}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                #
              </th>
              {createSortableHeader('API Key Name', 'name')}
              {createSortableHeader('Workspace', 'projectName')}
              {labelDefinitions.map((def, idx) => (
                <EditableColumnHeader
                  key={def.id}
                  labelDef={def}
                  index={idx}
                  onEdit={(id, name) => {
                    const updated = labelDefinitions.map(d =>
                      d.id === id ? { ...d, name } : d
                    );
                    saveLabelDefinitions(updated);
                    setLabelDefinitions(updated);
                  }}
                  onDelete={handleDeleteLabel}
                  onSort={() => handleSort(`label_${def.id}`)}
                  onReorder={handleReorderLabels}
                  sortKey={`label_${def.id}`}
                  currentSortKey={sort.key}
                  sortDir={sort.dir}
                />
              ))}
              {labelDefinitions.length < 5 && (
                <AddColumnButton
                  onAdd={(name, type, employeeField) => {
                    const newLabel: LabelDefinition = {
                      id: `label-${Date.now()}`,
                      name: name,
                      order: labelDefinitions.length,
                      type: type,
                      options: type === 'select' ? ['Option 1'] : undefined,
                      employeeField: employeeField,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    };

                    const updatedDefinitions = [...labelDefinitions, newLabel];
                    saveLabelDefinitions(updatedDefinitions);
                    setLabelDefinitions(updatedDefinitions);
                    window.dispatchEvent(new CustomEvent('labelSettingsChanged'));
                  }}
                  disabled={labelDefinitions.length >= 5}
                />
              )}
              {createSortableHeader('Requests', 'requests')}
              {createSortableHeader('Days', 'days', 'hidden md:table-cell')}
              {createSortableHeader('First Seen', 'firstSeen', 'hidden md:table-cell')}
              {createSortableHeader('Last Seen', 'lastSeen', 'hidden md:table-cell')}
              {createSortableHeader('Context Tokens', 'contextTokens', 'hidden lg:table-cell')}
              {createSortableHeader('Generated Tokens', 'generatedTokens', 'hidden lg:table-cell')}
              {createSortableHeader('Cached Tokens', 'cachedTokens', 'hidden lg:table-cell')}
              {createSortableHeader('Cost (click to toggle)', 'cost')}
              {createSortableHeader('Cached Cost', 'cachedCost', 'hidden xl:table-cell')}
              {createSortableHeader('Cached %', 'cachedPercentage', 'hidden xl:table-cell')}
              {createSortableHeader('Cost/100 Req', 'costPer100Requests', 'hidden lg:table-cell')}
              <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedApiKeys.map((apiKey, idx) => (
              <React.Fragment key={apiKey.id}>
                <tr
                  className={`cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'
                  }`}
                  onClick={() => setExpandedApiKey(expandedApiKey === apiKey.id ? null : apiKey.id)}
                >
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {currentOffset + idx + 1}
                  </td>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      <span>{expandedApiKey === apiKey.id ? '[-]' : '[+]'}</span>
                      <span>{apiKey.name}</span>
                      <ProviderBadge provider={apiKey.provider} />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(apiKey.name);
                          const button = e.currentTarget;
                          const originalTitle = button.title;
                          button.title = 'Copied!';
                          button.classList.add('text-green-600');
                          setTimeout(() => {
                            button.title = originalTitle;
                            button.classList.remove('text-green-600');
                          }, 1500);
                        }}
                        className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title="Copy API key"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <WorkspaceDisplay projectName={apiKey.projectName} projectIds={apiKey.projectIds} projectId={apiKey.projectId} keyId={apiKey.id} />
                  </td>
                  {labelDefinitions.map((def) => {
                    const metadataKey = apiKey.sourceIds?.find(id => apiKeyMetadata[id]) ?? apiKey.id;
                    const metadata = apiKeyMetadata[metadataKey] || {};
                    let value: string | number = metadata.labels?.[def.id] || '';
                    let cellClassName = '';
                    let cellTitle: string | undefined;

                    if (def.type === 'employee' && def.employeeField) {
                      const employee = findEmployeeForApiKey(apiKey);
                      if (employee) {
                        if (def.employeeField === 'department') {
                          const lines = getOrganizationLines(employee);
                          const info = getOrganizationInfo(employee);
                          value = lines.join('\n');
                          cellClassName = 'whitespace-pre-line leading-tight';
                          cellTitle = info.display !== '—' ? info.display : undefined;
                        } else {
                          const fieldValue = employee[def.employeeField];
                          value = (fieldValue === undefined || fieldValue === null || fieldValue === '')
                            ? '—'
                            : (fieldValue as string | number);
                        }
                      } else {
                        value = '—';
                      }
                    }

                    if (value === '') {
                      value = '—';
                    }

                    const shouldAllowWrap = cellClassName.includes('whitespace-pre-line');
                    const tdClassName = shouldAllowWrap
                      ? 'px-2 sm:px-3 py-1 sm:py-1.5 align-top text-xs text-gray-500 dark:text-gray-400'
                      : 'px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400';

                    return (
                      <td key={def.id} className={tdClassName} title={cellTitle}>
                        <EditableCell
                          value={value}
                          type={def.type}
                          options={def.options}
                          labelName={def.name}
                          employeeField={def.employeeField}
                          isReadOnly={def.type === 'employee'}
                          className={cellClassName}
                          onChange={(newValue) => {
                            if (def.type !== 'employee') {
                              updateApiKeyMetadata(metadataKey, {
                                apiKeyName: apiKey.name,
                                labels: {
                                  ...metadata.labels,
                                  [def.id]: newValue
                                }
                              });
                              setApiKeyMetadata(prev => ({
                                ...prev,
                                [metadataKey]: {
                                  ...prev[metadataKey],
                                  labels: {
                                    ...prev[metadataKey]?.labels,
                                    [def.id]: newValue
                                  }
                                }
                              }));
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                  {labelDefinitions.length < 5 && (
                    <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400"></td>
                  )}
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatNumber(apiKey.requests)}</td>
                  <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{apiKey.days}</td>
                  <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{apiKey.firstSeen}</td>
                  <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{apiKey.lastSeen}</td>
                  <td className="hidden lg:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatNumber(apiKey.contextTokens)}</td>
                  <td className="hidden lg:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatNumber(apiKey.generatedTokens)}</td>
                  <td className="hidden lg:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatNumber(apiKey.cachedTokens)}</td>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <CurrencyAmount amount={apiKey.cost} className="text-sm" />
                  </td>
                  <td className="hidden xl:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <CurrencyAmount amount={apiKey.cachedCost} className="text-sm" />
                  </td>
                  <td className="hidden xl:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {formatPercentage(apiKey.cachedPercentage)}
                  </td>
                  <td className="hidden lg:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <CurrencyAmount amount={apiKey.costPer100Requests} className="text-sm" />
                  </td>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <span className={`px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300`}>
                      {apiKey.requests === 0 ? '未使用' : apiKey.status}
                    </span>
                  </td>
                </tr>

                {expandedApiKey === apiKey.id && (
                  <tr className="bg-gray-50 dark:bg-gray-900">
                    <td colSpan={15 + labelDefinitions.length + (labelDefinitions.length < 5 ? 1 : 0)} className="px-2 sm:px-3 py-2">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Model Usage Details for {apiKey.name} ({
                              dateRange === '1hour' ? 'Last 1 hour' :
                              dateRange === '3hours' ? 'Last 3 hours' :
                              dateRange === '6hours' ? 'Last 6 hours' :
                              dateRange === '1day' ? 'Today' :
                              dateRange === 'yesterday' ? 'Yesterday' :
                              dateRange === '7days' ? 'Last 7 days' :
                              dateRange === '30days' ? 'Last 30 days' :
                              dateRange === '60days' ? 'Last 60 days' :
                              dateRange === '90days' ? 'Last 90 days' :
                              dateRange === '120days' ? 'Last 120 days' :
                              dateRange === '150days' ? 'Last 150 days' :
                              dateRange === '180days' ? 'Last 180 days' :
                              dateRange === '360days' ? 'Last 360 days' :
                              'Custom range'
                            })
                          </h4>
                          {apiKey.mergedSources && apiKey.mergedSources.length > 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSplit(apiKey.id);
                              }}
                              className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                                splitKeys.has(apiKey.id)
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'bg-transparent text-indigo-400 border-indigo-500 hover:bg-indigo-900/30'
                              }`}
                              title="Split model usage by source key / workspace"
                            >
                              {splitKeys.has(apiKey.id) ? 'Merged' : 'Split'}
                            </button>
                          )}
                        </div>
                        <ModelUsageContent apiKey={apiKey} isSplit={splitKeys.has(apiKey.id)} />
                        {shouldShowUserDailyCostChart && filteredData?.chartDailyUsage && filteredData.chartDailyUsage.length > 0 && filteredData.modelUsage && (
                          <UserDailyCostChart
                            apiKey={apiKey}
                            dailyUsage={filteredData.chartDailyUsage}
                            globalModelUsage={filteredData.modelUsage}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Buttons */}
      {(() => {
        const totalCount = filteredSortedApiKeys.length;
        const shouldShowPagination = pageSize > 0 && totalCount > pageSize;

        if (import.meta.env.DEV) debugLog('Pagination UI debug:', {
          totalCount,
          pageSize,
          shouldShowPagination,
          currentOffset
        });

        if (!shouldShowPagination) return null;

        return (
          <div className="px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-2">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Showing {currentOffset + 1}-{Math.min(currentOffset + pageSize, totalCount)} of {totalCount} API keys
            </div>
            <div className="flex gap-2">
              {currentOffset > 0 && (
                <button
                  onClick={() => {
                    const newOffset = Math.max(0, currentOffset - pageSize);
                    setCurrentOffset(newOffset);
                  }}
                  disabled={loading}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous {pageSize}
                </button>
              )}
              {currentOffset + pageSize < totalCount && (
                <button
                  onClick={() => {
                    const newOffset = currentOffset + pageSize;
                    setCurrentOffset(newOffset);
                  }}
                  disabled={loading}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next {Math.min(pageSize, totalCount - currentOffset - pageSize)}
                </button>
              )}
            </div>
          </div>
        );
      })()}
      {includesAnthropic && (
        <div className="px-2 sm:px-4 py-1 text-[11px] text-slate-500 dark:text-slate-400">
          ※Anthropic の「Requests」はトークン数からの推定値です（Claude Code: 25,000 tok/req、API: 5,000 tok/req）。
        </div>
      )}
    </div>
  );
};
